import {Component, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    IncidentApiService,
    IncidentResponse,
    RefundApiService,
    RefundGrounds,
    RefundRequest,
    RefundResponse,
    RefundStatus,
    RefundType,
    TransactionType,
} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, DATE_TIME_SERVER_FORMAT, download, LocalizationService, rxSpinner, Spinner} from '@npck/libs/commons';
import {DialogService, SnackbarService, StepperComponent} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {isEqual} from 'lodash-es';
import {combineLatest, debounceTime, distinctUntilChanged, mergeMap, Observable, of, startWith, take} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {AttachmentForm} from '../../../components/files/file/file.component';
import {createAttachmentsRequest} from '../../../components/files/files.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {INCIDENT_ID, REFUND_ID} from '../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../layout/main-container/main-container.component';
import {isAnyFormFillStarted} from '../../../utils/form.utils';
import {
    createParticipantRequest,
    ParticipantViewModel,
} from '../../incidents/create-edit/transaction-information-step/transcation-information/transaction-participant/transaction-participant.component';
import {RefundExecutionDialogComponent} from '../refund-details/refund-execution-dialog/refund-execution-dialog.component';
import {FINAL_REFUND_STATUSES} from '../refunds.component';
import {RefundCommonForm} from './refund-common-information-step/refund-common-information-step.component';
import {
    PreviewResolutionDialogData,
    RefundPreviewResolutionDialogComponent,
} from './refund-preview-resolution/refund-preview-resolution-dialog.component';
import {RefundTransactionForm} from './refund-transaction-step/refund-transaction-step.component';

const FORM_INITIALIZATION_DEBOUNCE = 1000;

@Component({
    selector: 'af-refunds-create-edit',
    standalone: false,
    templateUrl: './refunds-create-edit.component.html',
})
export class RefundsCreateEditComponent {
    @ViewChild('mainContainer') mainContainerComponent: MainContainerComponent;
    @ViewChild('stepper') stepper: StepperComponent;
    RefundType = RefundType;
    incident: IncidentResponse;
    refund: RefundResponse;
    incidentId: string;
    refundId: string;
    steps: Step[] = ['refundCommonInfo', 'refundTransaction'];
    currentStep: Step = 'refundCommonInfo';
    lastStep: Step = 'refundTransaction';
    buttonLabel: string;
    form: FormGroup<RefundForm>;
    isEditByInitiator: boolean;
    isEditByNpckAdmin: boolean;
    isEditBySender: boolean;
    sender: ParticipantViewModel;
    receiver: ParticipantViewModel;
    isSubmitting: boolean = false;
    private isFormChanged: boolean;
    private initialFormValue: RefundFormValue;

    constructor(
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private dialogService: DialogService,
        private incidentApiService: IncidentApiService,
        private formBuilder: FormBuilder,
        private refundApiService: RefundApiService,
        private localizationService: LocalizationService,
        private snackbarService: SnackbarService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.paramMap.get(INCIDENT_ID);
        this.refundId = this.activatedRoute.snapshot.paramMap.get(REFUND_ID);
        if (this.incidentId && !this.refundId) {
            this.incidentApiService.getIncident(this.incidentId)
                .pipe(rxSpinner('initial'))
                .subscribe(incident => {
                    this.buildForm(incident);
                    this.isEditByInitiator = true;
                    if (this.refundId) {
                        this.subscribeToFormInitializations();
                    } else {
                        this.subscribeToFormChanges();
                    }
                });
        } else if (this.refundId) {
            this.refundApiService.getRefund(this.refundId)
                .pipe(rxSpinner('initial'),
                    mergeMap(refund => combineLatest([of(refund), this.incidentApiService.getIncident(refund.incidentId)])))
                .subscribe(([refund, incident]) => {
                    this.sender = {...refund.sender, refundBin: refund.senderBin};
                    this.receiver = {...refund.receiver, refundBin: refund.receiverBin};
                    this.buildForm(incident, refund);
                    this.incidentId = incident.id;
                    const isProcessingOnMySide = refund.processingBin === this.authHolderService.organizationBin;

                    this.isEditByNpckAdmin = this.authHolderService.hasAccess(['npck-admin'])
                        && !FINAL_REFUND_STATUSES.includes(this.refund.status);
                    this.isEditByInitiator = isProcessingOnMySide && refund.processingBin === refund.initiatorBin &&
                        [RefundStatus.DRAFT, RefundStatus.REJECTED].includes(refund.status);
                    this.isEditBySender = isProcessingOnMySide && refund.processingBin === refund.senderBin
                        && refund.status !== RefundStatus.DRAFT;

                    if (this.refundId) {
                        this.subscribeToFormInitializations();
                    } else {
                        this.subscribeToFormChanges();
                    }
                });
        }
    }

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }
        if (this.refundId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#RefundId', {[REFUND_ID]: this.refundId});
        }
        return path;
    };

    navigateBack = (): Observable<boolean> => {
        if ((!this.refundId && isAnyFormFillStarted(this.form)) || (this.refundId && this.isFormChanged)) {
            return this.dialogService.openDialog({
                title: 'i18n_af_dialogCreateIncidentWarningBeforeExitTitle',
                subtitle: 'i18n_af_dialogCreateRefundWarningBeforeExitSubtitle',
                buttonOkText: 'i18n_af_buttonExit',
                buttonCancelText: 'i18n_af_buttonCancel',
                isButtonOkAccent: true,
            }, {disableClose: true});
        } else {
            return of(true);
        }
    };

    openCancellationWarningDialog(): void {
        this.navigateBack()
            .subscribe(result => {
                if (result) {
                    this.mainContainerComponent.breadcrumbs.navigateBackSkipListener();
                }
            });
    }

    previewResolution(): void {
        this.dialogService.openCustomDialogFitContent(RefundPreviewResolutionDialogComponent, <PreviewResolutionDialogData>{
            refundRequest: this.createRefundRequest(),
        })
            .subscribe();
    }

    submitWithStatus(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        if (this.authHolderService.hasAccess(['npck-admin'])
            && this.refundId && this.authHolderService.organizationBin !== this.refund?.processingBin
            && !FINAL_REFUND_STATUSES.includes(this.refund.status)
        ) {
            this.submit(this.refund?.status);
            return;
        }
        if (this.form.getRawValue().commonForm.refundType === RefundType.PROSECUTOR_ORDER) {
            this.dialogService.openDialog({
                title: 'i18n_af_dialogSendToProsecutorConfirmationDialogTitle',
                subtitle: 'i18n_af_dialogSendToProsecutorConfirmationDialogSubtitle',
                buttonOkText: 'i18n_af_buttonSignAndSend',
                buttonCancelText: 'i18n_af_buttonCancel',
            })
                .subscribe(result => {
                    if (result) {
                        this.submit(RefundStatus.PENDING_APPROVAL);
                    }
                });
        } else if (this.form.getRawValue().transactionForm.sender.accountIssuerBin === this.authHolderService.organizationBin) {
            this.dialogService.openCustomDialogFitContent<RefundExecutionDialogComponent, FormGroup<AttachmentForm>>(
                RefundExecutionDialogComponent,
                null,
                {autoFocus: false},
            )
                .subscribe(result => {
                    if (!!result) {
                        this.form.controls.commonForm.controls.attachments.controls.attachments.push(result);
                        this.submit(RefundStatus.PENDING_CONFIRMATION);
                    }
                });
        } else {
            this.submit(RefundStatus.IN_EXECUTION);
        }
    }

    submit(status: RefundStatus = RefundStatus.DRAFT): void {
        if (this.isSubmitting) { return; }
        this.isSubmitting = true;

        const request$ = this.refund
            ? this.refundApiService.updateRefund(this.refundId, this.createRefundRequest(status))
            : this.refundApiService.createRefund(this.createRefundRequest(status));

        request$
            .pipe(finalize(() => this.isSubmitting = false))
            .subscribe({
                next: (response) => {
                    if (response.url) {
                        window.location.href = response.url;
                    } else {
                        this.mainContainerComponent.breadcrumbs.navigateBackSkipListener();
                        const message = this.refund ? 'i18n_af_snackBarMessageRefundUpdated' : 'i18n_af_snackBarMessageRefundCreated';
                        this.snackbarService.showSnackBar(message, 'success');
                    }
                },
            });
    }

    updateBySender(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        const transactionForm = this.form.getRawValue().transactionForm;
        this.refundApiService.updateRefund(this.refund.id, {
            ...this.refund,
            isStatusChange: false,
            sender: createParticipantRequest(transactionForm.sender),
            receiver: createParticipantRequest(transactionForm.receiver),
            transactionAmount: transactionForm.amount,
            transactionCurrency: transactionForm.currency,
            transactionAmountInKzt: transactionForm.amountInKzt,
            attachments: [
                ...this.refund.attachments,
                ...createAttachmentsRequest({attachments: this.form.getRawValue().commonForm.attachments.attachments}),
            ],
        },
        )
            .subscribe({
                next: () => this.mainContainerComponent.breadcrumbs.navigateBackSkipListener(),
            });
    }

    downloadDocument(): void {
        download('assets/documents/Руководство_пользователя_Возврат_ДС.pdf');
    }

    private createRefundRequest(status: RefundStatus = RefundStatus.DRAFT): RefundRequest {
        const commonFormValue = this.form.getRawValue().commonForm;
        const transactionFormValue = this.form.getRawValue().transactionForm;
        const documentNumber =
            commonFormValue.refundType === RefundType.PROSECUTOR_ORDER ? commonFormValue.resolutionNumber : commonFormValue.documentNumber;
        const documentDate =
            commonFormValue.refundType === RefundType.PROSECUTOR_ORDER ? commonFormValue.resolutionDate : commonFormValue.documentDate;
        const sender = transactionFormValue.sender;
        const receiver = transactionFormValue.receiver;
        return {
            incidentId: this.incidentId,
            type: commonFormValue.refundType as RefundType,
            grounds: commonFormValue.grounds as RefundGrounds,
            description: commonFormValue.description,
            attachments: createAttachmentsRequest(commonFormValue.attachments),
            initiatorBin: this.authHolderService.organizationBin,
            transactionType: transactionFormValue.type as TransactionType,
            transactionCurrency: transactionFormValue.currency,
            transactionAmount: transactionFormValue.amount,
            transactionAmountInKzt: transactionFormValue.amountInKzt,
            resolutionAmount: commonFormValue.refundAmount,
            resolutionCurrency: commonFormValue.currency,
            resolutionAmountInKzt: commonFormValue.refundAmountInKzt,
            prosecutorOfficeBin: commonFormValue.prosecutorOfficeBin,
            status: status,
            isStatusChange: !this.refund || this.refund.status !== status,
            senderBin: sender.refundBin,
            receiverBin: receiver.refundBin,
            sender: createParticipantRequest(sender),
            receiver: createParticipantRequest(receiver),
            transactionIds: commonFormValue.transactions.map(transaction => transaction.id),
            kuiNumber: documentNumber,
            kuiNumberDate: dayjs(documentDate.date).format(DATE_SERVER_FORMAT),
            lawArticle: commonFormValue.lawNumber,
            lawArticleDate: commonFormValue.lawDate?.date ? dayjs(commonFormValue.lawDate.date).format(DATE_SERVER_FORMAT) : null,
            prosecutorFullName: commonFormValue.prosecutorFullName,
            prosecutorPosition: commonFormValue.prosecutorJobTitle,
            miaFullName: commonFormValue.officerFullName,
            miaPosition: commonFormValue.officerJobTitle,
            created_date: dayjs().format(DATE_TIME_SERVER_FORMAT),
            frontendHost: window.location.host,
        };
    }

    private buildForm(incident?: IncidentResponse, refund?: RefundResponse): void {
        this.form = this.formBuilder.group({});
        this.form.addControl('commonForm', this.formBuilder.group({}));
        this.form.addControl('transactionForm', this.formBuilder.group({}));
        this.incident = incident;
        this.refund = refund;
        if (!this.refundId) {
            this.form.valueChanges.subscribe(formValue => {
                if (formValue.commonForm?.transactions?.length) {
                    const sender = formValue.commonForm.transactions[0].sender;
                    const receiver = formValue.commonForm.transactions[0].receiver;
                    const senderBin = sender.card_mask.issuer_bin
                        ?? sender.account.issuerBin;
                    const receiverBin = receiver.card_mask.issuer_bin
                        ?? receiver.account.issuerBin;
                    this.sender = {
                        ...receiver,
                        refundBin: receiverBin,
                    };
                    this.receiver = {
                        ...sender,
                        refundBin: senderBin,
                    };
                }
            });
        }
        Spinner.hide('initial');
    }

    private subscribeToFormInitializations(): void {
        this.form.statusChanges.pipe(
            startWith(this.form.status),
            rxSpinner('initForms'),
            debounceTime(FORM_INITIALIZATION_DEBOUNCE),
            take(1),
        ).subscribe(() => {
            this.initialFormValue = this.form.getRawValue();
            this.subscribeToFormChanges();
        });
    }

    private subscribeToFormChanges(): void {
        this.form.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
        ).subscribe(() => {
            const rawValue = this.form.getRawValue();
            if (this.initialFormValue) {
                this.isFormChanged = !isEqual(this.initialFormValue, rawValue);
            }
            if (this.authHolderService.hasAccess(['npck-admin'])
                && this.refundId && this.authHolderService.organizationBin !== this.refund?.processingBin
                && !FINAL_REFUND_STATUSES.includes(this.refund.status)) {
                this.buttonLabel = 'i18n_af_buttonSave';
                return;
            }
            switch (rawValue.commonForm.refundType) {
                case RefundType.PROSECUTOR_ORDER:
                    this.buttonLabel = 'i18n_af_buttonSendToApproval';
                    break;
                case RefundType.VOLUNTARY:
                    this.buttonLabel = 'i18n_af_buttonSendToExecution';
                    break;
            }
        });
    }
}

declare type RefundFormValue = ɵTypedOrUntyped<RefundForm, ɵFormGroupRawValue<RefundForm>, any>;

export interface RefundForm {
    commonForm?: FormGroup<RefundCommonForm>;
    transactionForm?: FormGroup<RefundTransactionForm>;
}

declare type Step = 'refundCommonInfo' | 'refundTransaction';
